import { pageTitle } from '@palaccount/config';
import { INCOTERMS } from '@palaccount/contracts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { Typeahead } from '@/components/ui/typeahead';
import { useOrgSetting } from '@/features/admin/api';
import { useCurrencyTable } from '@/features/fx/api';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { displayRate } from '@/lib/money';
import { useCompanyToday } from '@/lib/session';

import { usePurchaseOrder, usePurchaseOrderPicker } from './api';
import { useCreateImportOrder } from './imports-api';
import { PurchasesTabs } from './purchases-tabs';

type ShipMethod = 'sea' | 'air' | 'land';

/**
 * Wave 1C — PUR-06's arrival default by method: sea 30 days, air 7, land 3
 * after the ship date. A default, never a lock — the box stays typed over,
 * and the server still refuses an arrival before the shipping date
 * (ERR-PUR-06-01). Calendar-day arithmetic at UTC, so no timezone moves the
 * business date (N-16).
 */
export const TRANSIT_DAYS: Record<ShipMethod, number> = { sea: 30, air: 7, land: 3 };

export function defaultArrivalDate(shipDate: string, method: ShipMethod): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(shipDate)) return shipDate;
  const date = new Date(`${shipDate}T00:00:00Z`);
  // A calendar-day offset, not an amount.
  date.setUTCDate(date.getUTCDate() + TRANSIT_DAYS[method]);
  return date.toISOString().slice(0, 10);
}

/**
 * BR-PUR-06-01 — an import order is ALWAYS raised from a purchase order, and
 * the link never changes afterwards. That is why this screen picks an order
 * rather than typing lines: the lines are the order's lines, and a shipment
 * that could be edited into disagreeing with what was ordered would make the
 * landed cost a fiction.
 *
 * Wave 1C — and the MONEY is the order's too. The currency is the picked
 * order's (the server refuses any other — `IMPORT_REFUSALS.currencyMismatch`)
 * and the rate is prefilled from what the company already knows: the order's
 * own frozen rate once it has one, else the FIN-05 table's current rate for
 * that currency. Both used to be literals (`ILS`, `3.6500`) that had nothing
 * to do with the order on the screen.
 */
export function ImportNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const warehouses = useWarehouses();
  const create = useCreateImportOrder();
  const { data: currencyTable } = useCurrencyTable();

  const today = useCompanyToday();
  /**
   * Wave 5E — where the shipment's lines come from. "Direct from supplier"
   * is offered only when the company has switched on
   * `purchasing.direct_import_enabled`; the server refuses it otherwise
   * (`IMPORT_REFUSALS.directImportDisabled`).
   */
  const directEnabled = useOrgSetting<boolean>('purchasing.direct_import_enabled');
  const [mode, setMode] = useState<'order' | 'direct'>('order');
  const direct = directEnabled && mode === 'direct';
  const [supplierId, setSupplierId] = useState('');
  const [directCurrency, setDirectCurrency] = useState('');
  const [lines, setLines] = useState<DirectLine[]>([blankLine()]);
  const [form, setForm] = useState({
    manualRef: '',
    notes: '',
    purchaseOrderId: '',
    warehouseId: '',
    orderDate: today,
    shipDate: today,
    arriveDate: defaultArrivalDate(today, 'sea'),
    incoterm: 'CIF' as (typeof INCOTERMS)[number],
    shipMethod: 'sea' as ShipMethod,
    rate: '',
    carrier: '',
    trackingNo: '',
  });
  /** The reader typed the arrival date; the method/ship-date default stops moving it. */
  const [arrivalTyped, setArrivalTyped] = useState(false);
  /** The reader typed the rate; the order/table default stops moving it. */
  const [rateTyped, setRateTyped] = useState(false);

  useEffect(() => {
    document.title = pageTitle(t('imports.newTitle'));
  }, [t]);

  /*
   * GEN-50 — a shipment already exists for some orders, and the server
   * refuses a second (ERR: "This purchase order already has an import
   * order"). The picker stays UNFILTERED rather than pre-narrowed, so the
   * refusal explains itself instead of the order silently not being offered.
   *
   * The screen still opens on the first order it can see, exactly as the
   * `<select>` did — that default is now the first row of one page rather
   * than the first row of the whole register.
   */
  const orderPicker = usePurchaseOrderPicker(form.purchaseOrderId || null);
  const purchaseOrderId = form.purchaseOrderId || orderPicker.options[0]?.id || '';
  const warehouseId = form.warehouseId || warehouses.data?.[0]?.id || '';

  // The picked order itself: its currency fixes the shipment's, and its
  // frozen rate (once sent) is the best rate the company has for it.
  const { data: order } = usePurchaseOrder(!direct && purchaseOrderId ? purchaseOrderId : undefined);
  const supplierPicker = usePartnerPicker(supplierId || null, { type: 'supplier' });
  const supplier = supplierPicker.byId.get(supplierId);
  // A direct shipment is priced in the supplier's currency unless retyped.
  const currencyCode = direct ? directCurrency || supplier?.currency || '' : (order?.currency ?? '');
  const linesReady =
    lines.length > 0 &&
    lines.every((line) => line.itemId !== '' && Number(line.qty) > 0 && line.unitPrice.trim() !== '');
  const tableRate =
    currencyTable?.rows.find((row) => row.code === currencyCode)?.rate ?? null;
  const defaultRate = displayRate(order?.exchangeRate ?? tableRate) ?? '';
  const rate = rateTyped ? form.rate : defaultRate;

  // Arrival follows the ship date and the method until the reader types it.
  const arriveDate = arrivalTyped
    ? form.arriveDate
    : defaultArrivalDate(form.shipDate, form.shipMethod);

  return (
    <div className="space-y-4">
      <PurchasesTabs />
      <h1 className="text-2xl font-semibold">{t('imports.newTitle')}</h1>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>{t('imports.shipment')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {directEnabled && (
            <fieldset className="sm:col-span-2">
              <legend className="mb-1 text-sm font-medium">{t('imports.sourceLabel')}</legend>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="import-source"
                    checked={mode === 'order'}
                    onChange={() => setMode('order')}
                  />
                  {t('imports.sourceOrder')}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="import-source"
                    checked={mode === 'direct'}
                    onChange={() => setMode('direct')}
                  />
                  {t('imports.sourceDirect')}
                </label>
              </div>
            </fieldset>
          )}

          {direct ? (
            <>
              <div className="sm:col-span-2">
                <Label htmlFor="direct-supplier">{t('imports.colSupplier')}</Label>
                <Typeahead
                  id="direct-supplier"
                  label={t('imports.colSupplier')}
                  value={supplierId || null}
                  onChange={(id) => {
                    setSupplierId(id ?? '');
                    setDirectCurrency('');
                  }}
                  options={supplierPicker.options}
                  selected={supplierPicker.optionFor(supplierId)}
                  query={supplierPicker.query}
                  onQueryChange={supplierPicker.setQuery}
                  emptyLabel={t('picker.noMatchingSupplier')}
                  loading={supplierPicker.loading}
                />
                <p className="mt-1 text-xs text-muted-foreground">{t('imports.directHint')}</p>
              </div>
              <DirectLines lines={lines} onChange={setLines} currency={currencyCode} />
            </>
          ) : (
          <div className="sm:col-span-2">
            <Label htmlFor="po">{t('imports.fromOrder')}</Label>
            <Typeahead
              id="po"
              label={t('imports.fromOrder')}
              value={purchaseOrderId || null}
              onChange={(id) => setForm({ ...form, purchaseOrderId: id ?? '' })}
              options={orderPicker.options}
              selected={orderPicker.optionFor(purchaseOrderId)}
              query={orderPicker.query}
              onQueryChange={orderPicker.setQuery}
              emptyLabel={t('picker.noMatchingOrder')}
              loading={orderPicker.loading}
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('imports.fromOrderHint')}</p>
          </div>
          )}

          <div>
            <Label htmlFor="wh">{t('imports.warehouse')}</Label>
            <NativeSelect
              id="wh"
              value={warehouseId}
              onChange={(event) => setForm({ ...form, warehouseId: event.target.value })}
            >
              {warehouses.data?.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} · {warehouse.name}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div>
            <Label htmlFor="incoterm">{t('imports.incoterm')}</Label>
            {/* BR-PUR-06-21 — the six codes carry their meanings, here at the
                moment of choosing: "a beginner cannot tell them apart" is §5
                field 10's complaint about this exact list shown bare. The
                detail page's header Field shows the same meaning under the
                stored code. */}
            <NativeSelect
              id="incoterm"
              value={form.incoterm}
              onChange={(event) =>
                setForm({ ...form, incoterm: event.target.value as (typeof INCOTERMS)[number] })
              }
            >
              {INCOTERMS.map((term) => (
                <option key={term} value={term}>
                  {term} — {t(`imports.incoterm_${term}`)}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div>
            <Label htmlFor="order-date">{t('imports.orderDate')}</Label>
            <Input
              id="order-date"
              type="date"
              value={form.orderDate}
              onChange={(event) => setForm({ ...form, orderDate: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-ship-date">{t('imports.shipDate')}</Label>
            <Input
              id="new-ship-date"
              type="date"
              value={form.shipDate}
              onChange={(event) => setForm({ ...form, shipDate: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-arrive-date">{t('imports.arriveDate')}</Label>
            <Input
              id="new-arrive-date"
              type="date"
              value={arriveDate}
              onChange={(event) => {
                setArrivalTyped(true);
                setForm({ ...form, arriveDate: event.target.value });
              }}
            />
          </div>
          <div>
            <Label htmlFor="method">{t('imports.shipMethod')}</Label>
            <NativeSelect
              id="method"
              value={form.shipMethod}
              onChange={(event) =>
                setForm({ ...form, shipMethod: event.target.value as ShipMethod })
              }
            >
              <option value="sea">{t('imports.method_sea')}</option>
              <option value="air">{t('imports.method_air')}</option>
              <option value="land">{t('imports.method_land')}</option>
            </NativeSelect>
          </div>

          <div>
            <Label htmlFor="currency">{t('imports.currency')}</Label>
            {/* Fixed by the order (BR-PUR-03-07's shape on the shipment):
                shown, never typed — a reading, not a control (EC-19-59). */}
            {direct ? (
              // Wave 5E — no order to fix it: the supplier's currency, retypeable.
              <Input
                id="currency"
                className="font-mono uppercase"
                maxLength={3}
                value={currencyCode}
                onChange={(event) => setDirectCurrency(event.target.value.toUpperCase())}
              />
            ) : (
              <div
                id="currency"
                className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm"
              >
                <bdi dir="ltr">{currencyCode || '—'}</bdi>
              </div>
            )}
            {!direct && <p className="mt-1 text-xs text-muted-foreground">{t('imports.currencyHint')}</p>}
          </div>
          <div>
            <Label htmlFor="new-rate">{t('imports.rateUnits')}</Label>
            <Input
              id="new-rate"
              inputMode="decimal"
              className="font-mono"
              value={rate}
              onChange={(event) => {
                setRateTyped(true);
                setForm({ ...form, rate: event.target.value });
              }}
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('imports.rateHint')}</p>
          </div>

          <div>
            <Label htmlFor="new-carrier">{t('imports.carrier')}</Label>
            <Input
              id="new-carrier"
              value={form.carrier}
              onChange={(event) => setForm({ ...form, carrier: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-tracking">{t('imports.tracking')}</Label>
            <Input
              id="new-tracking"
              value={form.trackingNo}
              onChange={(event) => setForm({ ...form, trackingNo: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="new-manual-ref">{t('imports.manualRef')}</Label>
            <Input
              id="new-manual-ref"
              maxLength={60}
              value={form.manualRef}
              onChange={(event) => setForm({ ...form, manualRef: event.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="new-notes">{t('imports.notes')}</Label>
            <Textarea
              id="new-notes"
              maxLength={2000}
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </div>

          {create.isError && (
            <p role="alert" className="text-sm text-destructive sm:col-span-2">
              {(create.error as Error).message}
            </p>
          )}

          <div className="flex gap-2 sm:col-span-2">
            <Button
              disabled={
                (direct ? !supplierId || !linesReady : !purchaseOrderId) ||
                !warehouseId ||
                !currencyCode ||
                rate === '' ||
                create.isPending
              }
              onClick={() =>
                create.mutate(
                  {
                    ...(direct
                      ? {
                          supplierId,
                          lines: lines.map((line) => ({
                            itemId: line.itemId,
                            qty: line.qty,
                            unitPrice: line.unitPrice,
                          })),
                        }
                      : { purchaseOrderId }),
                    ...(form.manualRef.trim() ? { manualRef: form.manualRef.trim() } : {}),
                    ...(form.notes.trim() ? { notes: form.notes } : {}),
                    warehouseId,
                    orderDate: form.orderDate,
                    shipDate: form.shipDate,
                    arriveDate,
                    incoterm: form.incoterm,
                    shipMethod: form.shipMethod,
                    currencyCode,
                    rate,
                    ...(form.carrier.trim() ? { carrier: form.carrier.trim() } : {}),
                    ...(form.trackingNo.trim() ? { trackingNo: form.trackingNo.trim() } : {}),
                  },
                  { onSuccess: (created) => void navigate(`/purchases/imports/${created.id}`) },
                )
              }
            >
              {t('imports.createShipment')}
            </Button>
            <Button variant="ghost" onClick={() => void navigate('/purchases/imports')}>
              {t('common.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/** Wave 5E — one typed line of a direct import. */
interface DirectLine {
  key: number;
  itemId: string;
  qty: string;
  unitPrice: string;
}

let nextKey = 1;
function blankLine(): DirectLine {
  nextKey += 1;
  return { key: nextKey, itemId: '', qty: '', unitPrice: '' };
}

/**
 * Wave 5E — the lines of a DIRECT import: an item, a quantity and a unit
 * price in the shipment's currency, exactly what a purchase order line would
 * have carried. The goods land in stock, so every line names an item.
 */
function DirectLines({
  lines,
  onChange,
  currency,
}: {
  lines: DirectLine[];
  onChange: (lines: DirectLine[]) => void;
  currency: string;
}) {
  const { t } = useTranslation();
  const set = (index: number, patch: Partial<DirectLine>) =>
    onChange(lines.map((line, at) => (at === index ? { ...line, ...patch } : line)));
  return (
    <div className="space-y-2 sm:col-span-2">
      <p className="text-sm font-medium">{t('imports.lines')}</p>
      {lines.map((line, index) => (
        <DirectLineRow
          key={line.key}
          line={line}
          index={index}
          currency={currency}
          onChange={(patch) => set(index, patch)}
          onRemove={lines.length > 1 ? () => onChange(lines.filter((_, at) => at !== index)) : null}
        />
      ))}
      <Button variant="outline" size="sm" onClick={() => onChange([...lines, blankLine()])}>
        {t('imports.addLine')}
      </Button>
    </div>
  );
}

function DirectLineRow({
  line,
  index,
  currency,
  onChange,
  onRemove,
}: {
  line: DirectLine;
  index: number;
  currency: string;
  onChange: (patch: Partial<DirectLine>) => void;
  onRemove: (() => void) | null;
}) {
  const { t } = useTranslation();
  const picker = useItemPicker([line.itemId]);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="min-w-48 flex-1">
        <Typeahead
          id={`direct-item-${index}`}
          label={t('imports.colItem')}
          value={line.itemId || null}
          onChange={(id) => onChange({ itemId: id ?? '' })}
          options={picker.options}
          selected={picker.optionFor(line.itemId)}
          query={picker.query}
          onQueryChange={picker.setQuery}
          emptyLabel={t('picker.noMatchingItem')}
          loading={picker.loading}
        />
      </div>
      <Input
        aria-label={t('imports.colQty')}
        placeholder={t('imports.colQty')}
        inputMode="decimal"
        className="w-24 text-end font-mono"
        value={line.qty}
        onChange={(event) => onChange({ qty: event.target.value })}
      />
      <Input
        aria-label={t('imports.colUnitPrice', { currency })}
        placeholder={t('imports.colUnitPrice', { currency })}
        inputMode="decimal"
        className="w-32 text-end font-mono"
        value={line.unitPrice}
        onChange={(event) => onChange({ unitPrice: event.target.value })}
      />
      {onRemove && (
        <Button variant="ghost" size="sm" onClick={onRemove}>
          {t('imports.removeLine')}
        </Button>
      )}
    </div>
  );
}
